import {Section} from 'constants/NavConstants';
import * as GlobalSearchActions from 'search/GlobalSearchActions';
import * as net from 'util/Net';

import {
  FilterErrors,
  GLOBAL_SEARCH_URL,
  SearchParams,
  SearchResponse,
  SearchResults,
  SearchStatus,
  SearchType,
} from './SearchConstants';
import {sanitizeSearchParams} from './SearchUtil';

const NON_ARCHIVABLE_SEARCH_TYPES = [SearchType.WORKLIST];

// These search types shouldn't automatically sort starred items to the top of the listing.
export const IGNORE_STARRING_SEARCH_TYPES: Array<SearchType> = [SearchType.GLOBAL];

export const START_POLLING_INTERVAL: number = 500;
export const BACKOFF_THRESHOLD: number = 6;
export const BACKOFF_INCREMENT: number = 500;
export const MAX_POLLING_INTERVAL: number = 5000;

export function performSearch(
  type: SearchType,
  searchParams: SearchParams,
  filterErrors: FilterErrors
): Promise<SearchResponse> {
  // sanitizeSearchParams adds a filter to only show non-archived items
  // by default. However, some objects (like Worklists) cannot be archived and
  // don't have a concept of archiving, so we must leave that filter off in those cases.
  const includeArchiveFilter = !NON_ARCHIVABLE_SEARCH_TYPES.includes(type);
  const sanitizedSearchParams = sanitizeSearchParams(searchParams, filterErrors, {
    includeArchiveFilter,
    includeIsAssociatedWithUnsubmittedRequestV2SubmissionFilter: true,
    includeProcessesIsSystemDataFilter: true,
  });
  return GlobalSearchActions.listItems(sanitizedSearchParams, type);
}

export async function pollSearchStatus(searchId: string): Promise<SearchResults> {
  const url = `${GLOBAL_SEARCH_URL}/poll`;
  let result = await net.postJSONPromise({url, data: {searchId}});
  let attempt = 1;
  while (result.status === SearchStatus.IN_PROGRESS) {
    const multiplier = Math.max(attempt - BACKOFF_THRESHOLD, 0);
    const duration = Math.min(START_POLLING_INTERVAL + BACKOFF_INCREMENT * multiplier, MAX_POLLING_INTERVAL);

    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, duration));

    result = await net.postJSONPromise({url, data: {searchId}});

    attempt++;
  }

  return GlobalSearchActions.globalSearchResponseToSearchResults(result);
}

export const SEARCH_TYPE_BY_SECTION: {[section: string]: SearchType} = {
  [Section.ANALYTICS]: SearchType.ANALYSES_AND_ANALYTICS_DASHBOARDS,
  [Section.BENCHLING_APPS]: SearchType.BENCHLING_APPS,
  [Section.CONNECTIONS]: SearchType.CONNECTIONS,
  [Section.JELLYFISH]: SearchType.JELLYFISH,
  [Section.REGISTRIES]: SearchType.BIOENTITIES,
  [Section.PROJECT_ITEMS]: SearchType.FOLDERS_AND_ITEMS,
  [Section.PROJECTS]: SearchType.PROJECTS,
  [Section.PROCESSES]: SearchType.PROCESS_MANAGEMENT,
  [Section.REQUESTS]: SearchType.REQUEST,
  [Section.REQUESTS_V2_SUBMISSIONS]: SearchType.REQUEST_V2_DASHBOARD,
  [Section.REQUESTS_V2_DEFINITIONS]: SearchType.REQUEST_V2_TYPES,
  [Section.SEARCH]: SearchType.GLOBAL,
  [Section.STORAGE]: SearchType.STORABLE,
  [Section.STUDIES]: SearchType.STUDIES,
  [Section.STUDY_ITEMS]: SearchType.STUDY_ITEMS,
  [Section.TEMPLATE_COLLECTIONS]: SearchType.TEMPLATE_COLLECTION,
  [Section.WORKFLOWS]: SearchType.WORKFLOW,
  [Section.WORKLISTS]: SearchType.WORKLIST,
  [Section.WORKLIST_ITEMS]: SearchType.WORKLIST_ITEMS,
};
